import { useState, useRef, useCallback } from 'react';
import { isValidInvoicePattern, invoicePatternErrorMessage } from './invoicePattern';

export const emptyClientFieldErrors = {
  client_name: '',
  display_as: '',
  invoice_pattern: '',
  email: '',
  phone: '',
  client_address: '',
  address_2: '',
  city: '',
  state: '',
  zip_code: '',
  request_notes: '',
};

export const ADD_CLIENT_FIELD_ORDER = [
  'client_name',
  'display_as',
  'invoice_pattern',
  'email',
  'phone',
  'client_address',
  'address_2',
  'city',
  'state',
  'zip_code',
  'request_notes',
];

export const EDIT_CLIENT_FIELD_ORDER = [
  'client_name',
  'display_as',
  'invoice_pattern',
  'client_address',
  'address_2',
  'city',
  'state',
  'zip_code',
  'email',
  'phone',
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ZIP_REGEX = /^\d{5}(-\d{4})?$/;

const trim = (value) => (typeof value === 'string' ? value.trim() : '');

const lengthError = (label, min, max) => `${label} must be between ${min} and ${max} characters`;

export function getClientFieldError(field, value) {
  switch (field) {
    case 'client_name': {
      const v = trim(value);
      if (v.length < 3 || v.length > 254) return lengthError('Client name', 3, 254);
      return '';
    }
    case 'display_as': {
      const v = trim(value);
      if (v.length < 3 || v.length > 254) return lengthError('Display as', 3, 254);
      return '';
    }
    case 'client_address': {
      const v = trim(value);
      if (v.length < 3 || v.length > 254) return lengthError('Client address', 3, 254);
      return '';
    }
    case 'city': {
      const v = trim(value);
      if (v.length < 2 || v.length > 254) return lengthError('City', 2, 254);
      return '';
    }
    case 'state': {
      const v = trim(value);
      if (v.length < 2 || v.length > 254) return lengthError('State', 2, 254);
      return '';
    }
    case 'zip_code': {
      const v = trim(value);
      if (!ZIP_REGEX.test(v)) return 'Zip code must be in US format (e.g., 12345 or 12345-6789)';
      return '';
    }
    case 'phone': {
      const v = typeof value === 'string' ? value : '';
      const digits = v.replace(/\D/g, '');
      if (digits.length !== 10) return 'Please enter a valid 10-digit US phone number';
      if (v.length < 6 || v.length > 128) return 'Phone must be between 6 and 128 characters';
      return '';
    }
    case 'email': {
      const v = trim(value);
      if (!v) return '';
      if (v.length > 254) return 'Email must be at most 254 characters';
      if (!EMAIL_REGEX.test(v)) return 'Email must be a valid email address';
      return '';
    }
    case 'address_2': {
      const v = trim(value);
      if (v.length > 254) return 'Address line 2 must not exceed 254 characters';
      return '';
    }
    case 'invoice_pattern': {
      if (!isValidInvoicePattern(value)) return invoicePatternErrorMessage;
      return '';
    }
    case 'request_notes': {
      const v = trim(value);
      if (v.length > 254) return 'Request notes must be at most 254 characters';
      return '';
    }
    default:
      return '';
  }
}

export function validateClientForm(client, { includeRequestNotes = false } = {}) {
  const fields = [
    'client_name',
    'display_as',
    'invoice_pattern',
    'email',
    'phone',
    'client_address',
    'address_2',
    'city',
    'state',
    'zip_code',
  ];
  if (includeRequestNotes) fields.push('request_notes');

  const errors = { ...emptyClientFieldErrors };
  fields.forEach((field) => {
    errors[field] = getClientFieldError(field, client?.[field]);
  });
  return errors;
}

export function hasClientFormErrors(errors) {
  return Object.values(errors).some(Boolean);
}

export function focusFirstInvalidClientField(errors, container, fieldOrder = ADD_CLIENT_FIELD_ORDER) {
  if (!container) return;
  const first = fieldOrder.find((field) => errors[field]);
  if (!first) return;
  const el = container.querySelector(`[name="${first}"]`);
  if (el && typeof el.focus === 'function') {
    el.focus();
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

export function useClientFormValidation({
  includeRequestNotes = false,
  fieldOrder = ADD_CLIENT_FIELD_ORDER,
} = {}) {
  const [errors, setErrors] = useState(emptyClientFieldErrors);
  const submittedRef = useRef(false);
  const errorsRef = useRef(errors);
  const formRef = useRef(null);
  errorsRef.current = errors;

  const handleFieldChange = useCallback((field, value) => {
    if (submittedRef.current || errorsRef.current[field]) {
      setErrors((prev) => ({ ...prev, [field]: getClientFieldError(field, value) }));
    }
  }, []);

  const handleFieldBlur = useCallback((field, value) => {
    setErrors((prev) => ({ ...prev, [field]: getClientFieldError(field, value) }));
  }, []);

  const revalidateFields = useCallback((values, fields) => {
    setErrors((prev) => {
      const next = { ...prev };
      fields.forEach((field) => {
        if (submittedRef.current || prev[field]) {
          next[field] = getClientFieldError(field, values[field]);
        }
      });
      return next;
    });
  }, []);

  const validateAll = useCallback((values) => {
    const next = validateClientForm(values, { includeRequestNotes });
    submittedRef.current = true;
    setErrors(next);
    if (hasClientFormErrors(next)) {
      setTimeout(() => {
        focusFirstInvalidClientField(next, formRef.current, fieldOrder);
      }, 0);
      return false;
    }
    return true;
  }, [includeRequestNotes, fieldOrder]);

  const resetValidation = useCallback(() => {
    submittedRef.current = false;
    setErrors(emptyClientFieldErrors);
  }, []);

  const applyDuplicateError = useCallback((message) => {
    const duplicateMessage = message || 'Client name already exists.';
    setErrors((prev) => ({
      ...prev,
      ...(/invoice pattern/i.test(duplicateMessage)
        ? { invoice_pattern: duplicateMessage }
        : { client_name: duplicateMessage }),
    }));
    return duplicateMessage;
  }, []);

  return {
    errors,
    setErrors,
    formRef,
    handleFieldChange,
    handleFieldBlur,
    revalidateFields,
    validateAll,
    resetValidation,
    applyDuplicateError,
  };
}
